import React, { useCallback, useContext, useMemo } from 'react';
import { Box } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from '../../_layouts/CommonLayout';
import Filters from './Filters';
import RiskPoolOrganizations from './RiskPoolOrganizations';
import ComplianceOverview from './ComplianceOverview';
import AgencyModificationsOverview from './AgencyModificationsOverview';
import { error, success, warning } from '_lex/_foundations/phxColors';
import useUrlParams from 'hooks/useUrlParams';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import { Routes, Route } from 'react-router-dom';
import RiskPoolOrganizationDetail from './RiskPoolOrganizations/Detail';
import { AppContext } from 'views/App/context';
import useAgencyModifications from './_data_sources/useAgencyModifications';
import useComplianceOverview from './_data_sources/useComplianceOverview';
import useRiskPoolOrganizations from './_data_sources/useRiskPoolOrganizations';

export const compliancePerformanceColors = {
  meetsAll: success[600],
  meetsMajority: warning[600],
  meetsSome: warning[400],
  meetsNone: error[700],
};

export const agencyModificationsColors = {
  YES: error[700],
  NO: success[600],
};

interface RiskPoolParams {
  search: string;
  startDate: string;
  endDate: string;
  trend: string;
  compliance: string;
  policyMods: string;
}

const defaultParams: RiskPoolParams = {
  search: '',
  startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
  trend: '',
  compliance: '',
  policyMods: '',
};

function RiskPoolDashboardMain() {
  const { currentOrganizationId } = useContext(AppContext);
  const [params, setParams] = useUrlParams(defaultParams);

  const updateParams = useCallback(
    (updatedParams: Partial<RiskPoolParams>) => {
      setParams((prev: RiskPoolParams) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const newParams = _.omitBy(
      Object.fromEntries(
        Object.entries({ ...params }).map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value])
      ),
      _.isEmpty
    );

    return newParams;
  }, [params]);

  const isFiltered = !_.isEmpty(_.omit(queryParams, ['startDate', 'endDate']));

  const organizationsQuery = useRiskPoolOrganizations(currentOrganizationId, queryParams);

  const organizations = useMemo(
    () => (organizationsQuery.data?.pages ? organizationsQuery.data.pages.flatMap((page) => page.organizations) : []),
    [organizationsQuery.data]
  );

  const totalCount = useMemo(
    () => organizationsQuery.data?.pages?.[0]?.totalCount ?? 0,
    [organizationsQuery.data]
  );

  const agencyModificationsQuery = useAgencyModifications(
    currentOrganizationId,
    {
      startDate: params.startDate,
      endDate: params.endDate,
      search: params.search,
      policyMods: params.policyMods,
    }
  );

  const complianceOverviewQuery = useComplianceOverview(
    currentOrganizationId,
    {
      startDate: params.startDate,
      endDate: params.endDate,
      search: params.search,
      compliance: params.compliance,
      policyMods: params.policyMods,
    }
  );

  const initialDateRange: DateRange<Dayjs> | null =
    params.startDate && params.endDate ? [dayjs(params.startDate), dayjs(params.endDate)] : null;

  return (
    <CommonLayout
      headerProps={{
        title: t('dashboard.riskPool.title'),
        subtitle: t('dashboard.riskPool.subtitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', minHeight: 0, gap: 4 }}>
        <Filters
          initialSearch={params.search}
          initialDateRange={initialDateRange}
          initialTrend={params.trend}
          initialCompliance={params.compliance}
          initialPolicyMods={params.policyMods}
          updateParams={updateParams}
          isFiltered={isFiltered}
          onClearFilters={clearParams}
        />
        <Box sx={{ display: 'grid', gridTemplateRows: 'auto 60vh', gap: 4, minHeight: 0, overflow: 'auto' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            <ComplianceOverview
              data={complianceOverviewQuery.data}
              isLoading={complianceOverviewQuery.isLoading}
              isFetching={complianceOverviewQuery.isFetching}
              isError={complianceOverviewQuery.isError}
              error={complianceOverviewQuery.error}
              updateParams={updateParams}
            />
            <AgencyModificationsOverview
              data={agencyModificationsQuery.data}
              isLoading={agencyModificationsQuery.isLoading}
              isFetching={agencyModificationsQuery.isFetching}
              isError={agencyModificationsQuery.isError}
              error={agencyModificationsQuery.error}
              updateParams={updateParams}
            />
          </Box>
          <RiskPoolOrganizations
            organizations={organizations}
            isLoading={organizationsQuery.isLoading}
            query={organizationsQuery}
            totalCount={totalCount}
            startDate={params.startDate}
            endDate={params.endDate}
          />
        </Box>
      </Box>
    </CommonLayout>
  );
}

const RiskPoolDashboard: React.FC = () => {
  return (
    <Routes>
      <Route
        path='/'
        element={<RiskPoolDashboardMain />}
      />
      <Route
        path=':orgId'
        element={<RiskPoolOrganizationDetail />}
      />
    </Routes>
  );
};

export default RiskPoolDashboard;
